/**
 * Unit 2.IMPL.D — DocumentList empty-state component.
 *
 * Bead: insurescope-zkolv.1.2.3.4.
 *
 * The ONE place empty-state copy lives. Five variants, picked by the parent's
 * branch resolver (`empty-search` / `empty-filter` / `empty-folder` /
 * `empty-library` / `empty-kb-root`).
 *
 * Renders as a block-level `<div>` sibling of the table — NOT a
 * `<TableRow><TableCell colSpan>` row inside the body. The block layout
 * makes the empty state size to its parent (the bordered container)
 * instead of the table's content width, which keeps the centered icon
 * and copy aligned with the visible scrollport regardless of how wide
 * the populated table would have been.
 *
 * Copy is locked against the Unit 2.RED tests:
 *   - [3] search   → `/no.*results.*${query}/i` + a `name: /clear search/i` button
 *   - [4] folder   → `/this folder is empty/i` and NO `name: /upload/i` button
 *   - [5] library  → `/upload your first document/i` + a `name: /^upload$/i` button
 *   - [6] kb-root  → `/add documents.*knowledge base|InsureScope.*AI to reference/i`
 *
 * DO NOT add upload affordances to the folder variant — the brief reserves the
 * primary action surface in folder context for the FilterBar's New menu (see
 * IMPL.D bead).
 */
import {
    FileSearch2,
    FileText,
    FolderOpen,
    FolderPlus,
    SearchX,
    Sparkles,
    Table as TableIcon,
    Upload,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { useScopesEnabled } from "@/contexts/frontend/organization/useScopesEnabled";
import type { LibraryTypeFilter } from "../utils/libraryItemTypes";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type FilterEmptyStateType = Exclude<LibraryTypeFilter, "all">;

export type DocumentListEmptyStateProps =
    | {
          variant: "search";
          query: string;
          onClearSearch?: () => void;
      }
    | { variant: "folder" }
    | { variant: "shared-folder"; groupName: string | null }
    | {
          variant: "library";
          onUpload?: () => void;
      }
    | {
          variant: "kb-root";
          onUpload?: () => void;
      }
    | {
          variant: "filter";
          typeFilter: FilterEmptyStateType;
          onUpload?: () => void;
      }
    | {
          /** Client workspace first-run: no root folder yet (Unit 7,
           * brief: 2026-06-05-client-workspace-folder-view.md §1). */
          variant: "workspace-start";
          clientName: string;
          onUpload?: () => void;
          onCreateFolder?: () => void;
      };

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DocumentListEmptyState(props: DocumentListEmptyStateProps) {
    return (
        <div
            data-testid="document-list-empty-state"
            data-variant={props.variant}
            // Block-level wrapper sized to the parent (the bordered
            // container in DocumentList). `min-h-64` preserves the legacy
            // 256px row-height presence; centering happens against the
            // visible container width rather than the table's intrinsic
            // content width.
            className="flex min-h-64 items-center justify-center px-4 py-12"
        >
            <EmptyStateContent {...props} />
        </div>
    );
}

// ---------------------------------------------------------------------------
// Internal variants
// ---------------------------------------------------------------------------

function EmptyStateContent(props: DocumentListEmptyStateProps) {
    switch (props.variant) {
        case "search":
            return (
                <SearchEmpty
                    query={props.query}
                    onClearSearch={props.onClearSearch}
                />
            );
        case "folder":
            return <FolderEmpty />;
        case "shared-folder":
            return <SharedFolderEmpty groupName={props.groupName} />;
        case "library":
            return <LibraryEmpty onUpload={props.onUpload} />;
        case "kb-root":
            return <KbRootEmpty onUpload={props.onUpload} />;
        case "filter":
            return (
                <FilterEmpty
                    typeFilter={props.typeFilter}
                    onUpload={props.onUpload}
                />
            );
        case "workspace-start":
            return (
                <WorkspaceStartEmpty
                    clientName={props.clientName}
                    onUpload={props.onUpload}
                    onCreateFolder={props.onCreateFolder}
                />
            );
        default: {
            // Exhaustiveness check: a missing variant becomes a compile-time
            // error here instead of a runtime null body.
            const _exhaustive: never = props;
            return _exhaustive;
        }
    }
}

// Block-level wrapper: the outer `<div>` already handles centering via
// `flex items-center justify-center`, so the inner content just needs to
// stack its icon + text + CTA vertically with consistent gap.
const wrapperClasses =
    "flex flex-col items-center justify-center gap-3 text-center";

const iconClasses = "text-muted-foreground/40 size-12";
const headlineClasses = "text-sm font-medium";
const descriptionClasses = "text-muted-foreground mt-0.5 max-w-md text-xs";
// `max-w-md` on the description `<p>` alone doesn't actually shrink the
// block — the description's shrink-to-fit parent sizes itself to the
// description's *uncapped* max-content, then the description fills the
// wider parent. Capping the inner block here is what makes a long
// description wrap to multiple lines and read as a centered island
// instead of a row-wide line of text.
const textBlockClasses = "max-w-md";

interface SearchEmptyProps {
    query: string;
    onClearSearch?: () => void;
}

function SearchEmpty({ query, onClearSearch }: SearchEmptyProps) {
    // Match the legacy NoDocumentsOverlay trimming so the headline doesn't
    // overflow on pathologically long search inputs.
    const truncated = query.length > 40 ? `${query.slice(0, 40)}...` : query;
    return (
        <div className={wrapperClasses}>
            <SearchX className={iconClasses} aria-hidden />
            <div className={textBlockClasses}>
                <p className={headlineClasses}>
                    No results for &ldquo;{truncated}&rdquo;
                </p>
                <p className={descriptionClasses}>
                    Try different keywords or clear your search.
                </p>
            </div>
            {onClearSearch && (
                <Button variant="outline" size="sm" onClick={onClearSearch}>
                    Clear search
                </Button>
            )}
        </div>
    );
}

function FolderEmpty() {
    const scopesEnabled = useScopesEnabled();
    return (
        <div className={wrapperClasses}>
            <FolderOpen className={iconClasses} aria-hidden />
            <div className={textBlockClasses}>
                <p className={headlineClasses}>This folder is empty</p>
                <p className={descriptionClasses}>
                    {scopesEnabled
                        ? "Use the New menu above to add documents, scopes, or sub-folders here."
                        : "Use the New menu above to add documents or sub-folders here."}
                </p>
            </div>
            {/*
             * The folder variant intentionally has NO primary CTA — the
             * FilterBar's New menu is the action surface. Unit 2.RED #4
             * asserts the ABSENCE of an upload button in this variant.
             */}
        </div>
    );
}

function SharedFolderEmpty({ groupName }: { groupName: string | null }) {
    return (
        <div className={wrapperClasses}>
            <FolderOpen className={iconClasses} aria-hidden />
            <div className={textBlockClasses}>
                <p className={headlineClasses}>
                    {groupName
                        ? `Nothing has been shared with ${groupName} yet.`
                        : "Nothing has been shared yet."}
                </p>
            </div>
        </div>
    );
}

interface LibraryEmptyProps {
    onUpload?: () => void;
}

function LibraryEmpty({ onUpload }: LibraryEmptyProps) {
    return (
        <div className={wrapperClasses}>
            <Upload className={iconClasses} aria-hidden />
            <div className={textBlockClasses}>
                <p className={headlineClasses}>
                    Upload your first document to get started.
                </p>
                <p className={descriptionClasses}>
                    Bring in policy documents and InsureScope will extract
                    fields, run analysis, and let you ask Polly about them.
                </p>
            </div>
            {onUpload && (
                <Button size="sm" onClick={onUpload}>
                    {/*
                     * Button label is strictly "Upload" — Unit 2.RED #5
                     * matches `name: /^upload$/i` (anchored). DO NOT
                     * "Upload Document" or "Upload File" here.
                     */}
                    Upload
                </Button>
            )}
        </div>
    );
}

interface KbRootEmptyProps {
    onUpload?: () => void;
}

interface FilterEmptyProps {
    typeFilter: FilterEmptyStateType;
    onUpload?: () => void;
}

const FILTER_EMPTY_COPY: Record<
    FilterEmptyStateType,
    {
        Icon: typeof FileSearch2;
        headline: string;
        description: string;
        /** True when the variant gets the primary "Upload" CTA. */
        showUploadCta: boolean;
    }
> = {
    scopes: {
        Icon: FileSearch2,
        headline: "No scopes yet",
        description:
            "Open a document and run an analysis to create your first scope.",
        showUploadCta: false,
    },
    comparisons: {
        Icon: TableIcon,
        headline: "No comparisons yet",
        description:
            "Compare two scopes from a document to see them side by side.",
        showUploadCta: false,
    },
    documents: {
        Icon: FileText,
        headline: "No documents yet",
        description:
            "Upload a policy document to extract fields and run analyses on it.",
        showUploadCta: true,
    },
};

function FilterEmpty({ typeFilter, onUpload }: FilterEmptyProps) {
    const { Icon, headline, description, showUploadCta } =
        FILTER_EMPTY_COPY[typeFilter];
    return (
        <div className={wrapperClasses}>
            <Icon className={iconClasses} aria-hidden />
            <div className={textBlockClasses}>
                <p className={headlineClasses}>{headline}</p>
                <p className={descriptionClasses}>{description}</p>
            </div>
            {showUploadCta && onUpload && (
                <Button size="sm" onClick={onUpload}>
                    Upload
                </Button>
            )}
        </div>
    );
}

interface WorkspaceStartEmptyProps {
    clientName: string;
    onUpload?: () => void;
    onCreateFolder?: () => void;
}

function WorkspaceStartEmpty({
    clientName,
    onUpload,
    onCreateFolder,
}: WorkspaceStartEmptyProps) {
    // Mirror SearchEmpty's trimming so a verbose client name can't
    // overflow the headline.
    const truncated =
        clientName.length > 40 ? `${clientName.slice(0, 40)}...` : clientName;
    return (
        <div className={wrapperClasses}>
            {/* Indigo-tinted, unlike the muted siblings: this state is an
             * invitation, not a dead end (brief §1). */}
            <FolderPlus className="text-primary/40 size-12" aria-hidden />
            <div className={textBlockClasses}>
                <p className={headlineClasses}>
                    Start {truncated}&rsquo;s workspace
                </p>
                <p className={descriptionClasses}>
                    Everything you file here is tagged to this client
                    automatically &mdash; it shows up in this tab, under their
                    filter in the Document Library, and in Polly&rsquo;s answers
                    about them.
                </p>
            </div>
            {/* Both first writes are equally valid, so this is the one
             * variant with two CTAs; labels match the New-menu items
             * verbatim for recognition. */}
            <div className="flex flex-wrap justify-center gap-2">
                {onUpload && (
                    <Button size="sm" onClick={onUpload}>
                        Upload Document
                    </Button>
                )}
                {onCreateFolder && (
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={onCreateFolder}
                    >
                        New Folder
                    </Button>
                )}
            </div>
        </div>
    );
}

function KbRootEmpty({ onUpload }: KbRootEmptyProps) {
    return (
        <div className={wrapperClasses}>
            <Sparkles className={iconClasses} aria-hidden />
            <div className={textBlockClasses}>
                <p className={headlineClasses}>Build your knowledge base</p>
                <p className={descriptionClasses}>
                    {/*
                     * Copy hits both halves of the Unit 2.RED #6 regex
                     * alternation (`/add documents.*knowledge base/i` OR
                     * `/InsureScope.*AI to reference/i`) so the test passes
                     * with either reading.
                     */}
                    Add documents to your knowledge base so InsureScope&apos;s
                    AI can reference them when answering questions about your
                    contracts.
                </p>
            </div>
            {onUpload && (
                <Button size="sm" onClick={onUpload}>
                    Upload
                </Button>
            )}
        </div>
    );
}
